import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';
import ProcessDiagram from '@/components/ProcessDiagram';
import TechFlowVideo from '@/components/TechFlowVideo';

export default function BrandPhilosophySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-12 md:py-16 bg-background-50 overflow-hidden">
      {/* Floating shapes */}
      <FloatingShapes count={16} color="secondary" />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={10} color="accent" />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-secondary-500/6 rounded-full blur-3xl animate-[morphFloat_8s_ease-in-out_infinite]" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl animate-[morphPulse_7s_ease-in-out_infinite_1s]" />

      {/* Animated decorative elements */}
      <div className="absolute top-20 right-[15%] w-12 h-12 border border-secondary-300/20 rounded-lg rotate-45 animate-[morphFloat_6s_ease-in-out_infinite]" />
      <div className="absolute bottom-32 left-[10%] w-8 h-8 border border-accent-300/20 rounded-full animate-[morphPulse_5s_ease-in-out_infinite_2s]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div
            className={`text-center mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-secondary-100/80 rounded-full text-[10px] font-mono font-medium text-secondary-700 uppercase tracking-widest mb-4">
              {t('howwe.work.philosophy.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight max-w-3xl mx-auto">
              {t('howwe.work.philosophy.title1')}{' '}
              <span className="text-accent-500">{t('howwe.work.philosophy.title2')}</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            {/* Comparison Cards */}
            <div className="space-y-6">
              {/* Traditional Card */}
              <div
                className={`relative p-6 md:p-8 rounded-xl border border-background-200/80 bg-background-50/80 backdrop-blur-sm transition-all duration-700 hover:shadow-lg hover:shadow-background-200/50 hover:-translate-y-1 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
              >
                {/* Geometric grid decoration */}
                <div className="absolute top-4 right-4 w-24 h-24 opacity-[0.06]">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <line key={`h-${i}`} x1="0" y1={i * 25} x2="100" y2={i * 25} stroke="currentColor" strokeWidth="0.5" />
                    ))}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <line key={`v-${i}`} x1={i * 25} y1="0" x2={i * 25} y2="100" stroke="currentColor" strokeWidth="0.5" />
                    ))}
                  </svg>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-foreground-100 flex items-center justify-center">
                    <i className="ri-settings-3-line text-lg text-foreground-400" />
                  </div>
                  <div>
                    <p className="text-xs font-mono text-foreground-400 uppercase tracking-wider">{t('howwe.work.traditional.label')}</p>
                    <p className="text-sm font-heading font-semibold text-foreground-700">{t('howwe.work.traditional.sub')}</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t('howwe.work.traditional.desc')}
                </p>

                {/* Animated arrow */}
                <div className="mt-4 flex items-center gap-2 text-foreground-400">
                  <i className="ri-arrow-down-line animate-bounce" />
                  <span className="text-xs font-mono">{t('howwe.work.traditional.arrow')}</span>
                </div>
              </div>

              {/* MorphenIQ Card */}
              <div
                className={`relative p-6 md:p-8 rounded-xl border border-accent-200/60 bg-accent-50/30 backdrop-blur-sm transition-all duration-700 hover:shadow-lg hover:shadow-accent-100/50 hover:-translate-y-1 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: '0.2s' }}
              >
                {/* Animated hexagonal corner element */}
                <div className="absolute top-4 right-4">
                  <svg width="48" height="48" viewBox="0 0 48 48" className="animate-[morphPulse_4s_ease-in-out_infinite]">
                    <polygon
                      points="24,4 42,14 42,34 24,44 6,34 6,14"
                      fill="none"
                      stroke="oklch(var(--accent-400))"
                      strokeWidth="1"
                      opacity="0.3"
                    />
                    <polygon
                      points="24,12 34,18 34,30 24,36 14,30 14,18"
                      fill="none"
                      stroke="oklch(var(--secondary-400))"
                      strokeWidth="0.5"
                      opacity="0.4"
                    />
                  </svg>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-shapes-line text-lg text-accent-600" />
                  </div>
                  <div>
                    <p className="text-xs font-mono text-accent-600 uppercase tracking-wider">{t('howwe.work.morpheniq.label')}</p>
                    <p className="text-sm font-heading font-semibold text-foreground-800">{t('howwe.work.morpheniq.sub')}</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {t('howwe.work.morpheniq.desc')}
                </p>

                {/* Animated arrow */}
                <div className="mt-4 flex items-center gap-2 text-accent-500">
                  <i className="ri-arrow-down-line animate-bounce" />
                  <span className="text-xs font-mono font-medium">{t('howwe.work.morpheniq.arrow')}</span>
                </div>
              </div>

              {/* Core philosophy statement */}
              <div
                className={`p-5 rounded-lg bg-primary-500 text-background-50 transition-all duration-1000 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: '0.4s' }}
              >
                <p className="text-sm md:text-base font-heading font-medium leading-relaxed">
                  {t('howwe.work.statement1')}{' '}
                  <span className="text-accent-300">{t('howwe.work.statement2')}</span>
                </p>
              </div>
            </div>

            {/* Right: Image + Process Diagram */}
            <div
              className={`flex flex-col gap-8 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '0.3s' }}
            >
              {/* Animated tech visualization replacing static image */}
              <div className="relative rounded-xl overflow-hidden group">
                <div className="aspect-[4/3] relative bg-primary-500/5">
                  <TechFlowVideo mode="card" color="accent" density={1} />
                  {/* Gradient overlays for depth */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-accent-500/10 pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-500/15 to-transparent pointer-events-none" />
                </div>

                {/* Floating label on image */}
                <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-background-50/90 backdrop-blur-sm rounded-md border border-background-200/60">
                  <span className="text-[10px] font-mono text-foreground-600 uppercase tracking-wider">
                    {t('howwe.work.fromRigid')}
                  </span>
                </div>
              </div>

              {/* Mini process flow */}
              <div className="p-6 rounded-xl border border-background-200/60 bg-background-50/80">
                <p className="text-xs font-mono text-foreground-500 uppercase tracking-widest mb-4">
                  {t('howwe.work.method')}
                </p>
                <ProcessDiagram />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div
            className={`mt-12 text-center transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.6s' }}
          >
            <Link
              to="/shape-your-project"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-all hover:scale-[1.02]"
            >
              {t('cta.shapeYourProject')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}